import { createContext, useContext } from 'react';
import type { ComponentPropsWithoutRef, ElementType } from 'react';
import clsx from 'clsx';

import { Switch as HeadlessSwitch } from '@headlessui/react';
import type {
    SwitchGroupProps as HeadlessSwitchGroupProps,
    SwitchProps as HeadlessSwitchProps
} from '@headlessui/react';

type DataContextType = Pick<HeadlessSwitchProps<'button'>, 'disabled' | 'checked'>;

const SwitchContext = createContext<DataContextType>(null);

function SwitchWrapper({
    checked = false,
    disabled = false,
    className,
    ...restProps
}: HeadlessSwitchGroupProps<'div'> & DataContextType) {
    return (
        <SwitchContext.Provider value={{ checked, disabled }}>
            <HeadlessSwitch.Group
                as="div"
                className={clsx('flex items-center space-x-3', className)}
                {...restProps}
            />
        </SwitchContext.Provider>
    );
}

Switch.Wrapper = SwitchWrapper;

type SwitchLabelProps = ComponentPropsWithoutRef<'label'> & {
    as?: ElementType;
    passive?: boolean;
};

function SwitchLabel({ className, ...restProps }: SwitchLabelProps) {
    const { checked, disabled } = useContext(SwitchContext);
    return (
        <HeadlessSwitch.Label
            className={clsx(
                'cursor-pointer text-xs !ring-0',
                {
                    'text-icoBlack dark:text-white': checked,
                    'text-icoGray-400 dark:text-icoDarkMode-wolf': !checked,
                    'cursor-not-allowed': disabled
                },
                className
            )}
            {...restProps}
        />
    );
}

Switch.Label = SwitchLabel;

export type SwitchProps = Omit<HeadlessSwitchProps<'button'>, 'disabled' | 'checked'> & {
    ariaLabel?: string | JSX.Element;
    dotClassName?: {
        checked?: string;
        unchecked?: string;
        additionalClasses?: string;
    };
};

function Switch({ className, ariaLabel, dotClassName, ...restProps }: SwitchProps) {
    const { checked, disabled } = useContext(SwitchContext);
    return (
        <HeadlessSwitch
            data-testid="switch-action"
            disabled={disabled}
            checked={checked}
            className={clsx(
                'relative inline-flex h-5 w-9 items-center rounded-full',
                {
                    'bg-icoBlue-100 dark:bg-icoBlue/20': checked,
                    'bg-icoGray-300 dark:bg-white/20': !checked,
                    'cursor-not-allowed': disabled
                },
                className
            )}
            {...restProps}>
            <span className="sr-only">{ariaLabel}</span>
            <span
                className={clsx(
                    'inline-block h-3 w-3 transform rounded-full transition duration-200 ease-in-out',
                    {
                        [dotClassName?.checked ?? 'translate-x-5 bg-icoBlue']: checked,
                        [dotClassName?.unchecked ??
                        'translate-x-1 bg-icoGray-400 dark:bg-icoDarkMode-wolf']: !checked
                    },
                    dotClassName?.additionalClasses
                )}
            />
        </HeadlessSwitch>
    );
}

export default Switch;
